import {DEFAULT_DOCS_LINK_HOSTNAME} from './constants'

/**
 * Determine if a value is nullish (undefined or null)
 * @param {*} value The value to check
 * @returns boolean
 */
export function isDefined<T = unknown>(value?: T): value is NonNullable<T> {
  return value !== null && value !== undefined && typeof value !== 'undefined'
}

export function onDocumentReady(initializer: () => void) {
  if (document.readyState === 'complete') {
    initializer()
  } else {
    document.addEventListener('DOMContentLoaded', initializer)
  }
}

export function decodeHTML(html: string) {
  // Textareas decode character references but treat markup as text.
  const decoder = document.createElement('textarea')
  decoder.innerHTML = html
  return decoder.value
}

class InvariantViolationError extends Error {}

export function invariant(condition: any, message?: string): asserts condition {
  if (condition) return

  throw new InvariantViolationError(message ?? 'Invariant violation')
}

export const assertElement = (node: any): node is Element => node instanceof Element

export const assertHTMLElement = (node: Element | ParentNode | HTMLElement | null): node is HTMLElement =>
  node instanceof HTMLElement

export const assertSVGElement = (node: SVGElement | SVGSVGElement | null): node is SVGElement =>
  node instanceof SVGElement

export const getGitHubDocsHostname = () =>
  document.body.getAttribute('data-github-docs-hostname') || DEFAULT_DOCS_LINK_HOSTNAME

/**
 * Determine if the source hostname matches the target hostname or is a subdomain of it. To match, the source
 * hostname must either be exactly equal to the target hostname or be a subdomain of it. This avoids the
 * Public-Suffix-List pitfalls of comparing only the last two domain labels, which would let unrelated tenants
 * sharing a suffix (e.g. `attacker.ghe.com` and `victim.ghe.com`) be treated as same-site.
 *
 * Note: this check is hostname-only and intentionally ignores scheme and port. It is not a strict origin
 * comparison (scheme + host + port).
 *
 * Inputs may be bare hostnames or `host` values from the URL API (which can include a `:port` suffix, and IPv6
 * addresses wrapped in square brackets). Both forms are normalized before comparison.
 */
export const isSameOrigin = (targetHostname: string, sourceHostname: string) => {
  const normalize = (h: string) => {
    // Bracketed IPv6 with optional port (e.g. "[::1]:3000" → "::1")
    if (h.startsWith('[')) return h.replace(/^\[([^\]]+)\](?::\d+)?$/, '$1').toLowerCase()
    // Unbracketed IPv6 contains multiple colons — no port suffix to strip
    if (h.indexOf(':') !== h.lastIndexOf(':')) return h.toLowerCase()
    // Regular hostname with optional port suffix (e.g. "example.com:3000" → "example.com")
    return h.replace(/:\d+$/, '').toLowerCase()
  }
  const target = normalize(targetHostname)
  const source = normalize(sourceHostname)
  return source === target || source.endsWith(`.${target}`)
}

/** Adds `target="_parent"` (if not already set to something else) to all links in this node. */
export const openLinksInParent = (node: Element) => {
  for (const link of node.querySelectorAll('a'))
    if (!link.hasAttribute('_target')) link.setAttribute('target', '_parent')
}
